import { __awaiter } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { EventEnum } from '@as-react/common-types';
import { EventUtil, getCurrencyISOCode } from '@as-react/common-utils';
import { Box } from '@yonderland/andes-react';
import { useTranslation } from 'react-i18next';
import ApplePayAPI from '../../api/ApplePayAPI';
import { FASCIA_GROUP } from '../../constants/GeneralConstants';
import { I18N_NAMESPACES } from '../../constants/i18nConstants';
import { APPLE_PAY_PAYMENT_FAILED } from '../../constants/StorageKeysConstants';
import { useApplePayButton } from '../../hooks/useApplePayButton';
import ApplePayUtil from '../../util/ApplePayUtil';
import SessionStorageUtil from '../../util/SessionStorageUtil';
import { HtmlInclude } from '../common/html-include/HtmlInclude';
export const ApplePayButton = ({ onClick, totalPrices: { totalBasketDiscountAmount, totalPriceSell, priceTotalBasket, priceDelivery }, hasAndOnlyDigitalProduct, userEmail, clickAndCollectStoreId, storeName, totalVoucherAmount = 0, innerRef, }) => {
    const { t } = useTranslation(I18N_NAMESPACES.buy);
    const { servicesEndpoint, market, currency, defaultCountryCode, mainWebShop, applePayMerchantId } = useAemConfig();
    const { applePaySupportedNetworks } = useApplePayButton();
    let updatedData;
    const getContactFields = (billing = false) => {
        const contactFields = ['name'];
        if (billing) {
            contactFields.push('postalAddress');
            return contactFields;
        }
        if (!hasAndOnlyDigitalProduct) {
            contactFields.push('phone');
        }
        if (!storeName && !hasAndOnlyDigitalProduct) {
            contactFields.push('postalAddress');
        }
        if (!userEmail) {
            contactFields.push('email');
        }
        return contactFields;
    };
    const initiateCheckout = () => __awaiter(void 0, void 0, void 0, function* () {
        yield ApplePayAPI.initiateCheckout(servicesEndpoint, clickAndCollectStoreId);
    });
    const validateMerchant = (session, validationURL) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const validateMerchantData = {
                url: validationURL,
                merchantIdentifier: applePayMerchantId,
                displayName: ApplePayUtil.defaultConfig.APPLE_PAY_DISPLAY_NAME,
                initiative: ApplePayUtil.defaultConfig.APPLE_PAY_MERCHANT_INITIATIVE,
                initiativeContext: window.location.hostname,
            };
            const merchantSession = yield ApplePayAPI.validateMerchant(servicesEndpoint, validateMerchantData, market);
            if (!('merchantIdentifier' in merchantSession &&
                'merchantSessionIdentifier' in merchantSession &&
                ('nOnce' in merchantSession || 'nonce' in merchantSession))) {
                session.abort();
            }
            session.completeMerchantValidation(merchantSession);
        }
        catch (_a) {
            session.abort();
        }
    });
    const updateShippingDetails = (session, contact) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const { newTotal, shippingCost, discount, subTotal, totalVoucherAmount: voucherAmount, } = yield ApplePayAPI.updateShippingAddress(servicesEndpoint, contact);
            updatedData = Object.assign(Object.assign({}, updatedData), { newTotal: Object.assign(Object.assign({}, updatedData.newTotal), { amount: newTotal.toString() }), newLineItems: [
                    {
                        label: t('buy.payment.applepay.subtotal'),
                        amount: subTotal.toString(),
                    },
                    {
                        label: t('buy.payment.applepay.discount'),
                        amount: discount.toString(),
                    },
                    {
                        label: t('buy.payment.applepay.delivery'),
                        amount: shippingCost.toString(),
                    },
                    {
                        label: t('buy.payment.applepay.voucher'),
                        amount: voucherAmount.toString(),
                    },
                ] });
            delete updatedData.errors;
            session.completeShippingContactSelection(updatedData);
        }
        catch (_a) {
            updatedData.errors = [
                new ApplePayError('shippingContactInvalid', 'countryCode', t('buy.payment.applepay.error.countrycode')),
            ];
            session.completeShippingContactSelection(updatedData);
        }
    });
    const paymentAuthorized = (session, payment) => __awaiter(void 0, void 0, void 0, function* () {
        const updatedPaymentData = Object.assign(Object.assign({}, payment), { shippingContact: userEmail
                ? Object.assign(Object.assign({}, payment.shippingContact), { emailAddress: userEmail }) : payment.shippingContact });
        try {
            const { redirectUrl } = yield ApplePayAPI.payment(servicesEndpoint, updatedPaymentData);
            if (redirectUrl === null || redirectUrl === void 0 ? void 0 : redirectUrl.includes('payment')) {
                SessionStorageUtil.setItem(APPLE_PAY_PAYMENT_FAILED, true);
                session.completePayment({
                    status: ApplePaySession.STATUS_FAILURE,
                });
            }
            else {
                session.completePayment({
                    status: ApplePaySession.STATUS_SUCCESS,
                });
            }
            if (redirectUrl) {
                location.href = redirectUrl;
            }
        }
        catch (_a) {
            EventUtil.dispatch(EventEnum.APPLE_PAY_PAYMENT_FAILED);
            session.completePayment({
                status: ApplePaySession.STATUS_FAILURE,
            });
        }
    });
    const clickHandler = () => {
        const PAYMENT_REQUEST = Object.assign(Object.assign({ countryCode: defaultCountryCode.toUpperCase(), currencyCode: getCurrencyISOCode(currency), merchantCapabilities: ApplePayUtil.defaultConfig.APPLE_PAY_MERCHANT_CAPABILITIES }, (storeName && {
            shippingMethods: [
                {
                    identifier: ApplePayUtil.defaultConfig.APPLE_PAY_SHIPPING_METHOD_IDENTIFIER,
                    label: storeName,
                    amount: '0',
                    detail: '',
                },
            ],
        })), { shippingType: storeName
                ? ApplePayUtil.defaultConfig.APPLE_PAY_SHIPPING_TYPE.storePickup
                : ApplePayUtil.defaultConfig.APPLE_PAY_SHIPPING_TYPE.shipping, supportedNetworks: applePaySupportedNetworks, requiredBillingContactFields: getContactFields(true), requiredShippingContactFields: getContactFields(), lineItems: [
                {
                    label: t('buy.payment.applepay.subtotal'),
                    amount: totalPriceSell,
                },
                {
                    label: t('buy.payment.applepay.discount'),
                    amount: totalBasketDiscountAmount,
                },
                {
                    label: t('buy.payment.applepay.delivery'),
                    amount: priceDelivery,
                },
                {
                    label: t('buy.payment.applepay.voucher'),
                    amount: totalVoucherAmount.toString(),
                },
            ], total: {
                label: FASCIA_GROUP[mainWebShop],
                amount: priceTotalBasket,
            } });
        updatedData = {
            newTotal: PAYMENT_REQUEST.total,
            newLineItems: PAYMENT_REQUEST.lineItems,
        };
        const session = new ApplePaySession(ApplePayUtil.defaultConfig.APPLE_PAY_VERSION, PAYMENT_REQUEST);
        session.begin();
        session.onvalidatemerchant = (event) => __awaiter(void 0, void 0, void 0, function* () {
            yield initiateCheckout();
            yield validateMerchant(session, event.validationURL);
        });
        session.onshippingcontactselected = (event) => __awaiter(void 0, void 0, void 0, function* () {
            yield updateShippingDetails(session, event.shippingContact);
        });
        session.onpaymentauthorized = (event) => __awaiter(void 0, void 0, void 0, function* () {
            yield paymentAuthorized(session, event.payment);
        });
        onClick === null || onClick === void 0 ? void 0 : onClick();
    };
    return (_jsxs(Box, { innerRef: innerRef, children: [_jsx(HtmlInclude, { styleFragment: ApplePayUtil.getApplePayButtonStyles() }), _jsx("button", { type: "button", className: "apple-pay-button apple-pay-button-black", onClick: clickHandler })] }));
};
